/*
 * Houndkeep cart rewards, cart upsells and bundles (Dawn 16 add-on).
 *
 * Ground rules (see snippets/houndkeep-rewards.liquid for the full contract):
 * - The cart on the SERVER is the source of truth. Progress bars and messages
 *   are rendered by Liquid: anniversary tiers from the tier base
 *   (`cart.items_subtotal_price`, capped by the undiscounted non-gift-card total,
 *   i.e. before order-level discounts) and free shipping from `cart.total_price`
 *   (after discounts). This file never computes or displays a discount amount;
 *   Shopify automatic discounts decide what is actually applied at checkout.
 * - Items are added one real variant id per line via /cart/add.js, so each line
 *   keeps the variant/SKU that the CJ fulfilment app is mapped to. There is no
 *   synthetic bundle product.
 * - The pure helpers are unit-tested in Node (work/claude-tests). The only
 *   browser global is `window.HoundkeepRewards` (those helpers + init), which
 *   also guards against double initialisation; there are no test-only hooks.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // The script tag can be emitted by more than one snippet; wire up once.
    if (!window.HoundkeepRewards) {
      window.HoundkeepRewards = api;
      api.init();
    }
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Pure helpers                                                        */
  /* ------------------------------------------------------------------ */

  function isWholeNumber(value) {
    return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
  }

  function toNumber(value) {
    if (value === null || value === undefined || value === '') return 0;
    var n = typeof value === 'number' ? value : Number(value);
    return isFinite(n) ? n : NaN;
  }

  /**
   * Validates tier settings exactly like snippets/houndkeep-rewards.liquid:
   * a tier whose threshold or percent is 0/blank is switched off; the remaining
   * tiers must be whole numbers, percent <= 100, and strictly ascending in both
   * threshold and percent. Any violation makes the whole set invalid, because a
   * partially wrong tier table would advertise terms Shopify does not enforce.
   * @param {Array<{threshold:*, percent:*}>} raw
   * @returns {{valid:boolean, tiers:Array<{threshold:number, percent:number}>}}
   */
  function normalizeTiers(raw) {
    var tiers = [];
    var valid = true;
    var prevThreshold = 0;
    var prevPercent = 0;
    (Array.isArray(raw) ? raw : []).forEach(function (entry) {
      var threshold = toNumber(entry && entry.threshold);
      var percent = toNumber(entry && entry.percent);
      if (isNaN(threshold) || isNaN(percent)) {
        valid = false;
        return;
      }
      if (threshold <= 0 || percent <= 0) return; // tier switched off
      if (!isWholeNumber(threshold) || !isWholeNumber(percent) || percent > 100) valid = false;
      if (threshold <= prevThreshold || percent <= prevPercent) valid = false;
      prevThreshold = threshold;
      prevPercent = percent;
      tiers.push({ threshold: threshold, percent: percent });
    });
    if (tiers.length === 0) valid = false;
    return { valid: valid, tiers: valid ? tiers : [] };
  }

  /**
   * Highest tier reached (tiers never stack) and the next tier, for a
   * merchandise subtotal in minor units (cents).
   */
  function tierStatus(subtotalCents, tiers) {
    var subtotal = Math.max(0, Math.floor(toNumber(subtotalCents) || 0));
    var current = null;
    var next = null;
    (tiers || []).forEach(function (tier) {
      if (tier.threshold <= subtotal) current = tier;
      else if (!next) next = tier;
    });
    var remaining = next ? next.threshold - subtotal : 0;
    var progressPercent = next ? Math.min(100, Math.floor((subtotal * 100) / next.threshold)) : 100;
    return {
      subtotal: subtotal,
      current: current,
      next: next,
      remainingCents: remaining,
      progressPercent: progressPercent,
      isHighest: !!current && !next,
    };
  }

  /** Free-shipping progress on the cart total AFTER discounts (cents). */
  function shippingStatus(totalCents, thresholdCents) {
    var total = Math.max(0, Math.floor(toNumber(totalCents) || 0));
    var threshold = toNumber(thresholdCents);
    if (!isWholeNumber(threshold) || threshold <= 0) return { valid: false };
    var qualified = total >= threshold;
    return {
      valid: true,
      total: total,
      threshold: threshold,
      qualified: qualified,
      remainingCents: qualified ? 0 : threshold - total,
      progressPercent: qualified ? 100 : Math.min(100, Math.floor((total * 100) / threshold)),
    };
  }

  /** `allowed` is a comma separated list such as "USD" (theme setting). */
  function currencyAllowed(code, allowed) {
    if (!code) return false;
    var wanted = String(code).trim().toUpperCase();
    return String(allowed || '')
      .toUpperCase()
      .replace(/\s+/g, '')
      .split(',')
      .filter(Boolean)
      .indexOf(wanted) !== -1;
  }

  /**
   * Builds the /cart/add.js body. Every line must be a real, positive integer
   * variant id; duplicates are merged so one variant is never split across two
   * requests. Throws on anything else instead of guessing.
   */
  function buildAddPayload(lines) {
    if (!Array.isArray(lines) || lines.length === 0) throw new Error('hk:no-lines');
    var order = [];
    var byId = {};
    lines.forEach(function (line) {
      var idText = String(line && line.id !== undefined ? line.id : '').trim();
      if (!/^[1-9]\d*$/.test(idText)) throw new Error('hk:invalid-variant');
      var quantity = line.quantity === undefined || line.quantity === '' ? 1 : Number(line.quantity);
      if (!isWholeNumber(quantity) || quantity < 1) throw new Error('hk:invalid-quantity');
      if (byId[idText]) {
        byId[idText].quantity += quantity;
      } else {
        // Keep the id as a number only when it is safely representable.
        var asNumber = Number(idText);
        byId[idText] = { id: Number.isSafeInteger(asNumber) ? asNumber : idText, quantity: quantity };
        order.push(idText);
      }
    });
    return {
      items: order.map(function (key) {
        return byId[key];
      }),
    };
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var value = (Math.round(toNumber(cents) || 0) / 100).toFixed(precision);
    var parts = value.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  /**
   * Formats minor units with a Shopify money format such as "${{amount}}".
   * Falls back to Intl when no Shopify format is supplied.
   */
  function formatMoney(cents, format, currency, locale) {
    if (typeof format === 'string' && /\{\{\s*\w+\s*\}\}/.test(format)) {
      // Shops may wrap the format in markup (e.g. <span class=money>); the result
      // is written with textContent, so keep the text only.
      return format.replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, function (match, token) {
        switch (token) {
          case 'amount':
            return formatWithDelimiters(cents, 2, ',', '.');
          case 'amount_no_decimals':
            return formatWithDelimiters(cents, 0, ',', '.');
          case 'amount_with_comma_separator':
            return formatWithDelimiters(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return formatWithDelimiters(cents, 0, '.', ',');
          case 'amount_with_space_separator':
            return formatWithDelimiters(cents, 2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':
            return formatWithDelimiters(cents, 0, ' ', '');
          case 'amount_with_apostrophe_separator':
            return formatWithDelimiters(cents, 2, "'", '.');
          default:
            return formatWithDelimiters(cents, 2, ',', '.');
        }
      });
    }
    var amount = (toNumber(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  /** Message for a failed /cart/add.js response, never inventing a reason. */
  function cartErrorMessage(body, fallback) {
    if (body && typeof body === 'object') {
      if (typeof body.description === 'string' && body.description.trim()) return body.description.trim();
      if (typeof body.errors === 'string' && body.errors.trim()) return body.errors.trim();
      if (typeof body.message === 'string' && body.message.trim() && body.message !== 'Cart Error') {
        return body.message.trim();
      }
    }
    return fallback;
  }

  /** Must match `data-hk-signature` in snippets/houndkeep-rewards.liquid. */
  function cartSignature(cart) {
    if (!cart || typeof cart.total_price !== 'number' || typeof cart.items_subtotal_price !== 'number') return null;
    return [cart.items_subtotal_price, cart.total_price, cart.item_count, cart.currency].join(':');
  }

  /** Replaces [amount], [percent] ... tokens; unknown tokens stay visible. */
  function fillTemplate(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values || {}, key) ? String(values[key]) : match;
    });
  }

  /**
   * What adding a bundle would do to the merchandise subtotal. Returns the tier
   * the cart would sit in afterwards and whether that is higher than today.
   * This is a projection for messaging only; Shopify decides at checkout.
   */
  function projectBundle(cartSubtotalCents, bundleCents, tiers) {
    var before = tierStatus(cartSubtotalCents, tiers);
    var after = tierStatus((toNumber(cartSubtotalCents) || 0) + (toNumber(bundleCents) || 0), tiers);
    var beforePercent = before.current ? before.current.percent : 0;
    var afterPercent = after.current ? after.current.percent : 0;
    return { before: before, after: after, unlocksTier: afterPercent > beforePercent };
  }

  /**
   * Tier base for a /cart.js payload; must match houndkeep-rewards.liquid:
   * the lower of items_subtotal_price and the original price of all
   * non-gift-card lines (Shopify checks an order minimum against both).
   */
  function tierBase(cart) {
    if (!cart || typeof cart.items_subtotal_price !== 'number') return null;
    var original = ((cart.items || [])).reduce(function (sum, item) {
      return item && !item.gift_card ? sum + (toNumber(item.original_line_price) || 0) : sum;
    }, 0);
    return Math.min(cart.items_subtotal_price, original);
  }

  /** Map of variant id (string) -> quantity for a /cart.js payload. */
  function quantitiesByVariant(cart) {
    var map = {};
    ((cart && cart.items) || []).forEach(function (item) {
      var key = String(item.variant_id !== undefined ? item.variant_id : item.id);
      map[key] = (map[key] || 0) + (Number(item.quantity) || 0);
    });
    return map;
  }

  /**
   * Shopify does not document whether a multi-item add is all-or-nothing.
   * After a failed bundle add we compare the cart before and after and report
   * exactly which requested variants did land in the cart.
   */
  function addedVariantIds(before, after, requestedIds) {
    var a = quantitiesByVariant(before);
    var b = quantitiesByVariant(after);
    return (requestedIds || [])
      .map(String)
      .filter(function (id, index, list) {
        return list.indexOf(id) === index && (b[id] || 0) > (a[id] || 0);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Browser wiring                                                      */
  /* ------------------------------------------------------------------ */

  var LIVE_REGION_ID = 'HoundkeepRewardsLive';
  var state = { lastAnnounced: null, announceTimer: null, refreshController: null, journeys: {} };

  function routeUrl(name, fallback) {
    var routes = typeof window !== 'undefined' ? window.routes : null;
    return (routes && routes[name]) || fallback;
  }

  function hasPubSub() {
    return (
      typeof subscribe === 'function' && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
    );
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    return drawer && drawer.classList.contains('active') ? drawer : null;
  }

  /**
   * Polite live region. While the drawer (role=dialog, aria-modal) is open the
   * region must live INSIDE the dialog, otherwise screen readers that honour
   * aria-modal ignore it. Dawn re-renders the dialog's contents, so the region
   * is (re)created on demand and filled after a short delay.
   */
  function liveRegion() {
    var drawer = openDrawer();
    var host = drawer ? drawer.querySelector('.drawer__inner') : null;
    var id = host ? LIVE_REGION_ID + '-drawer' : LIVE_REGION_ID;
    var region = document.getElementById(id);
    if (region) return region;
    region = document.createElement('div');
    region.id = id;
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    (host || document.body).appendChild(region);
    return region;
  }

  function announce(message) {
    if (!message) return;
    var region = liveRegion();
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 150);
  }

  /** Text of the rewards panel the customer is looking at right now. */
  function currentRewardsText() {
    var nodes = Array.prototype.slice.call(document.querySelectorAll('[data-hk-region="rewards"]'));
    if (!nodes.length) return '';
    var drawerOpen = !!openDrawer();
    var primary =
      nodes.find(function (node) {
        var inDrawer = !!node.closest('cart-drawer');
        return drawerOpen ? inDrawer : !inDrawer;
      }) || nodes[0];
    return Array.prototype.slice
      .call(primary.querySelectorAll('[data-hk-say]'))
      .map(function (el) {
        return el.textContent.replace(/\s+/g, ' ').trim();
      })
      .filter(Boolean)
      .join(' ');
  }

  function updateJourneyMotion() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('[data-hk-journey]').forEach(function (node) {
      var key = node.getAttribute('data-hk-journey');
      var current = { progress: Number(node.getAttribute('data-hk-progress')), mood: Number(node.getAttribute('data-hk-mood')) };
      var previous = state.journeys[key];
      state.journeys[key] = current;
      if (!previous || reduce) return;
      var dog = node.querySelector('.hk-journey__companion');
      var width = node.getBoundingClientRect().width;
      if (dog && dog.animate && width && current.progress !== previous.progress) {
        var half = dog.getBoundingClientRect().width / 2;
        var position = function (progress) { return Math.max(half, Math.min(width - half, width * progress / 100)); };
        var offset = position(previous.progress) - position(current.progress);
        dog.animate([{ transform: 'translateX(calc(-50% + ' + offset + 'px))' }, { transform: 'translateX(-50%)' }], { duration: 420, easing: 'ease-out' });
        var fill = node.querySelector('.hk-journey__fill');
        if (fill && fill.animate) fill.animate([{ width: previous.progress + '%' }, { width: current.progress + '%' }], { duration: 420, easing: 'ease-out' });
      }
      if (current.mood > previous.mood) {
        node.classList.add('is-celebrating');
        window.setTimeout(function () { node.classList.remove('is-celebrating'); }, 1800);
      }
    });
  }

  function announceIfChanged() {
    updateJourneyMotion();
    var text = currentRewardsText();
    if (state.lastAnnounced === null) {
      state.lastAnnounced = text; // never announce the initial page state
      return;
    }
    if (text === state.lastAnnounced) return;
    state.lastAnnounced = text;
    announce(text);
  }

  function scheduleAnnounce() {
    window.clearTimeout(state.announceTimer);
    state.announceTimer = window.setTimeout(announceIfChanged, 400);
  }

  function parseHtml(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function fetchCart() {
    return fetch(routeUrl('cart_url', '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (response) {
      if (!response.ok) throw new Error('hk:cart-' + response.status);
      return response.json();
    });
  }

  /**
   * Rewards/upsell blocks that sit outside the regions Dawn re-renders itself
   * (e.g. a cart page without a Subtotal block) are refreshed here when their
   * server signature no longer matches the cart. Drawer blocks are always
   * re-rendered by Dawn's own cart-drawer code and are skipped.
   */
  function refreshStaleRegions(signature) {
    if (!signature) return Promise.resolve();
    var stale = Array.prototype.slice
      .call(document.querySelectorAll('[data-hk-region][data-hk-section]'))
      .filter(function (node) {
        return !node.closest('cart-drawer') && node.getAttribute('data-hk-signature') !== signature;
      });
    if (!stale.length) return Promise.resolve();

    var bySection = {};
    stale.forEach(function (node) {
      var id = node.getAttribute('data-hk-section');
      (bySection[id] = bySection[id] || []).push(node);
    });

    if (state.refreshController) state.refreshController.abort();
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    state.refreshController = controller;

    return Promise.all(
      Object.keys(bySection).map(function (sectionId) {
        var url = routeUrl('cart_url', '/cart') + '?section_id=' + encodeURIComponent(sectionId);
        return fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
          .then(function (response) {
            if (!response.ok) throw new Error('hk:section-' + response.status);
            return response.text();
          })
          .then(function (html) {
            var doc = parseHtml(html);
            bySection[sectionId].forEach(function (node) {
              var fresh = node.id ? doc.getElementById(node.id) : null;
              if (fresh) node.replaceWith(document.importNode(fresh, true));
              else node.remove(); // e.g. cart emptied or feature switched off
            });
          });
      })
    )
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        // Keep the old (server-rendered) panel rather than inventing numbers.
        if (window.console) console.warn('[houndkeep] rewards refresh failed', error);
      })
      .then(scheduleAnnounce);
  }

  function onCartUpdate(event) {
    var data = event && event.cartData;
    var signature = cartSignature(data);
    // Let Dawn finish swapping its own sections first.
    window.setTimeout(function () {
      if (signature) {
        refreshStaleRegions(signature);
      } else {
        fetchCart()
          .then(function (cart) {
            return refreshStaleRegions(cartSignature(cart));
          })
          .catch(function () {});
      }
    }, 0);
    scheduleAnnounce();
  }

  /* ---------------------------- add to cart --------------------------- */

  function formLines(form) {
    return Array.prototype.slice.call(form.querySelectorAll('[data-hk-variant-input]')).map(function (input) {
      var line = input.closest('[data-hk-line]') || form;
      var qtyInput = line.querySelector('[data-hk-qty-input]');
      return { id: input.value, quantity: qtyInput ? qtyInput.value : 1 };
    });
  }

  /** Message text lives on the form or on a [data-hk-messages] wrapper (Liquid form tag). */
  function formMessage(form, name) {
    var attr = 'data-hk-msg-' + name;
    if (form.hasAttribute(attr)) return form.getAttribute(attr);
    var holder = form.closest('[data-hk-messages]');
    return holder ? holder.getAttribute(attr) : null;
  }

  function setFormStatus(form, kind, message) {
    var error = form.querySelector('[data-hk-error]');
    var status = form.querySelector('[data-hk-status]');
    if (error) {
      error.textContent = kind === 'error' ? message : '';
      error.hidden = kind !== 'error';
    }
    if (status) status.textContent = kind === 'status' ? message : '';
  }

  function setBusy(form, button, busy) {
    form.setAttribute('data-hk-busy', busy ? 'true' : 'false');
    form.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (!button) return;
    var label = button.querySelector('[data-hk-button-label]');
    // Dawn's .button.loading hides the label (color: transparent) and expects a
    // visible spinner, so show/hide the loading-spinner snippet explicitly.
    var spinner = button.querySelector('.loading__spinner');
    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
      if (label) {
        label.setAttribute('data-hk-idle-text', label.textContent);
        label.textContent = formMessage(form, 'adding') || label.textContent;
      }
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
      if (label && label.hasAttribute('data-hk-idle-text')) {
        label.textContent = label.getAttribute('data-hk-idle-text');
        label.removeAttribute('data-hk-idle-text');
      }
    }
  }

  /** Section ids of Dawn's cart page, or null when not on /cart. */
  function cartPageSections() {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (!items || !footer || !items.dataset.id || !footer.dataset.id) return null;
    return { items: items.dataset.id, footer: footer.dataset.id };
  }

  function cartPageSectionIds(page) {
    return [page.items, page.footer, 'cart-icon-bubble', 'cart-live-region-text'];
  }

  /**
   * Same swap Dawn's CartItems does after a quantity change: only the
   * `.js-contents` regions are replaced, so <cart-items> is not re-created
   * (no duplicate cart:view event) and the page heading stays in place.
   */
  function swapRegion(elementId, selector, html) {
    var host = document.getElementById(elementId);
    if (!host || typeof html !== 'string') return false;
    var target = (selector && host.querySelector(selector)) || host;
    var doc = parseHtml(html);
    var source = doc.querySelector(selector || '.shopify-section') || (selector ? doc.getElementById(elementId) : null);
    if (!source) return false;
    target.innerHTML = source.innerHTML;
    return true;
  }

  /** Applies a Section Rendering API map to the /cart page; false if a required region is missing. */
  function applyCartPageSections(page, sections) {
    var map = sections || {};
    var itemsOk = swapRegion('main-cart-items', '.js-contents', map[page.items]);
    var footerHost = document.getElementById('main-cart-footer');
    var footerOk = footerHost && footerHost.querySelector('.js-contents')
      ? swapRegion('main-cart-footer', '.js-contents', map[page.footer])
      : swapRegion('main-cart-footer', '#main-cart-footer', map[page.footer]);
    swapRegion('cart-icon-bubble', '.shopify-section', map['cart-icon-bubble']);
    swapRegion('cart-live-region-text', '.shopify-section', map['cart-live-region-text']);
    return itemsOk && footerOk;
  }

  function refreshCartPage() {
    var page = cartPageSections();
    if (!page) return Promise.resolve(false);
    return fetch(window.location.pathname + '?sections=' + cartPageSectionIds(page).map(encodeURIComponent).join(','), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('hk:sections-' + response.status);
        return response.json();
      })
      .then(function (map) {
        return applyCartPageSections(page, map);
      });
  }

  function focusElement(el) {
    if (!el) return false;
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus();
    return true;
  }

  function focusCartHeading() {
    focusElement(document.querySelector('cart-items .title--primary') || document.querySelector('cart-items .title'));
  }

  /** After an add from inside the open drawer: keep focus and the focus trap in the dialog. */
  function refocusDrawer() {
    var container = document.getElementById('CartDrawer');
    if (!container) return;
    var target =
      container.querySelector('#HoundkeepUpsells-drawer .hk-upsells__heading') ||
      container.querySelector('.drawer__inner');
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    if (typeof trapFocus === 'function') trapFocus(container, target);
    else target.focus();
  }

  function titleForVariant(form, variantId) {
    var inputs = Array.prototype.slice.call(form.querySelectorAll('[data-hk-variant-input]'));
    for (var i = 0; i < inputs.length; i += 1) {
      if (String(inputs[i].value) === String(variantId)) {
        var line = inputs[i].closest('[data-hk-line]');
        return (line && line.getAttribute('data-hk-title')) || '';
      }
    }
    return '';
  }

  /**
   * After a failed multi-item add, read the cart again and tell the customer
   * which requested items did get added (if any), then refresh the cart UI.
   * The alert is written exactly once, with the final message.
   */
  function reconcilePartialAdd(form, before, payload, message) {
    return fetchCart()
      .then(function (after) {
        var added = addedVariantIds(
          before,
          after,
          payload.items.map(function (item) {
            return item.id;
          })
        );
        if (!added.length) {
          setFormStatus(form, 'error', message);
          return;
        }
        var titles = added
          .map(function (id) {
            return titleForVariant(form, id);
          })
          .filter(Boolean);
        var partial = fillTemplate(
          formMessage(form, 'partial') || '[items] were added to your cart. Please review your cart.',
          { items: titles.join(', ') }
        );
        setFormStatus(form, 'error', message + ' ' + partial);
        var update = cartPageSections() ? refreshCartPage() : Promise.resolve(true);
        return update.then(function () {
          if (hasPubSub()) return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'houndkeep', cartData: after });
        });
      })
      .catch(function () {
        setFormStatus(form, 'error', message);
      });
  }

  function dispatchLinesUpdate(form, payload) {
    var events = window.StandardEvents || {};
    var LinesEvent = events.CartLinesUpdateEvent;
    if (!LinesEvent || typeof LinesEvent.createPromise !== 'function') return null;
    try {
      var deferred = LinesEvent.createPromise();
      form.dispatchEvent(
        new LinesEvent({
          action: 'add',
          context: form.getAttribute('data-hk-context') === 'upsell' ? 'cart' : 'product',
          lines: payload.items.map(function (item) {
            return { merchandiseId: String(item.id), quantity: item.quantity };
          }),
          promise: deferred.promise,
        })
      );
      return deferred;
    } catch (e) {
      return null;
    }
  }

  /** Settles the cart:lines-update promise with the real cart (analytics consumers wait on it). */
  function settleLines(deferred, cart, error) {
    if (!deferred) return;
    var LinesEvent = window.StandardEvents && window.StandardEvents.CartLinesUpdateEvent;
    try {
      if (cart && LinesEvent && typeof LinesEvent.createCartFromAjaxResponse === 'function') {
        deferred.resolve({ cart: LinesEvent.createCartFromAjaxResponse(cart) });
      } else {
        deferred.reject(error || new Error('hk:no-cart'));
      }
    } catch (e) {
      /* analytics only */
    }
  }

  function addFromForm(form, submitter) {
    if (form.getAttribute('data-hk-busy') === 'true') return Promise.resolve();
    var button = submitter || form.querySelector('[type="submit"]');
    // A form whose own submit button is hidden (Buy more, save more on the product page) names the visible button
    // that closing the cart drawer should return focus to.
    var returnId = form.getAttribute('data-hk-return-focus');
    var focusReturn = (returnId && document.getElementById(returnId)) || button;
    var fallbackError = formMessage(form, 'error') || 'Something went wrong. Please try again.';

    var payload;
    try {
      payload = buildAddPayload(formLines(form));
    } catch (e) {
      setFormStatus(form, 'error', fallbackError);
      return Promise.resolve();
    }

    // On the /cart page we always update the page in place (even when the
    // theme also has a drawer); elsewhere the drawer is used when present.
    var drawer = document.querySelector('cart-drawer');
    var page = cartPageSections();
    var useDrawer =
      !page &&
      !!(drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function');
    var drawerWasOpen = useDrawer && drawer.classList.contains('active');
    var body = { items: payload.items };
    if (useDrawer) {
      body.sections = drawer.getSectionsToRender().map(function (section) {
        return section.id;
      });
      body.sections_url = window.location.pathname;
    } else if (page) {
      body.sections = cartPageSectionIds(page);
      body.sections_url = window.location.pathname;
    }

    setFormStatus(form, 'none', '');
    setBusy(form, button, true);
    var linesDeferred = dispatchLinesUpdate(form, payload);
    var succeeded = false;
    var isMulti = payload.items.length > 1;
    var before = null;

    // Multi-item adds: remember the cart first so a partial add can be reported honestly.
    var snapshot = isMulti
      ? fetchCart()
          .then(function (cart) {
            before = cart;
          })
          .catch(function () {
            before = null;
          })
      : Promise.resolve();

    return snapshot
      .then(function () {
        return fetch(routeUrl('cart_add_url', '/cart/add') + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
      })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return null;
          })
          .then(function (data) {
            return { ok: response.ok, data: data };
          });
      })
      .then(function (result) {
        var data = result.data;
        if (!result.ok || !data || data.status) {
          var message = cartErrorMessage(data, fallbackError);
          settleLines(linesDeferred, null, new Error(message));
          if (isMulti && before) return reconcilePartialAdd(form, before, payload, message);
          setFormStatus(form, 'error', message);
          return;
        }
        succeeded = true;
        var addedMessage = formMessage(form, 'added') || '';

        if (!useDrawer && !page) {
          // Cart notification / page cart type, not on /cart: Dawn's notification
          // popup can only describe one product, so go to /cart - after the
          // cart:lines-update promise has been settled for analytics.
          return fetchCart()
            .then(function (cart) {
              settleLines(linesDeferred, cart);
            })
            .catch(function (error) {
              settleLines(linesDeferred, null, error);
            })
            .then(function () {
              window.location.assign(routeUrl('cart_url', '/cart'));
            });
        }

        if (useDrawer) {
          var rendered = false;
          if (data.sections) {
            try {
              // Opened from elsewhere (e.g. a bundle): closing the drawer returns focus to the button.
              // Already open (an upsell inside it): keep the original opener.
              if (!drawerWasOpen && drawer.setActiveElement && focusReturn) drawer.setActiveElement(focusReturn);
              drawer.renderContents(data);
              drawer.classList.remove('is-empty');
              rendered = true;
            } catch (e) {
              rendered = false;
            }
          }
          if (!rendered && typeof drawer.open === 'function') drawer.open(focusReturn);
          if (!drawerWasOpen) {
            setFormStatus(form, 'status', addedMessage);
          }
        } else {
          if (!applyCartPageSections(page, data.sections)) {
            window.location.reload();
            return;
          }
          // The upsell that was clicked lives in the re-rendered footer, so
          // confirm through the live region and move focus to the page heading.
          announce(addedMessage);
          focusCartHeading();
        }

        // Dawn subscribers (quick-add-bulk, price-per-item) expect a full cart
        // payload, not the add response.
        return fetchCart()
          .then(function (cart) {
            settleLines(linesDeferred, cart);
            if (hasPubSub()) {
              return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'houndkeep', cartData: cart });
            }
          })
          .catch(function (error) {
            settleLines(linesDeferred, null, error);
          });
      })
      .catch(function (error) {
        if (succeeded) return;
        settleLines(linesDeferred, null, error);
        // A network failure can still happen after Shopify processed the add.
        if (isMulti && before) return reconcilePartialAdd(form, before, payload, fallbackError);
        setFormStatus(form, 'error', fallbackError);
      })
      .then(function () {
        // Dawn's cartUpdate subscribers can replace the upsells after the add
        // response has rendered. Wait for that publish promise before focusing
        // the final nodes, and respect a customer who has closed the drawer.
        if (succeeded && drawerWasOpen && drawer.classList.contains('active')) {
          refocusDrawer();
          announce(formMessage(form, 'added') || '');
        }
        // The form may have been re-rendered (drawer/page swap); only touch it if still attached.
        if (form.isConnected) setBusy(form, button, false);
        scheduleAnnounce();
      });
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-hk-add-form]')) return;
    if (typeof fetch !== 'function' || typeof JSON === 'undefined') return; // native POST fallback
    event.preventDefault();
    var button = event.submitter || form.querySelector('[type="submit"]');
    if (button && button.getAttribute('aria-disabled') === 'true') return;
    addFromForm(form, button);
  }

  /* ------------------------------ elements ---------------------------- */

  function setText(node, text) {
    // Only write when the text changes, so live regions don't re-announce.
    if (node && node.textContent !== text) node.textContent = text;
  }

  function defineElements() {
    if (typeof customElements === 'undefined') return;

    if (!customElements.get('houndkeep-upsells')) {
      /*
       * Upsells are rendered inside Dawn's <cart-drawer-items>, whose CartItems
       * class treats EVERY bubbling 'change' as a cart quantity edit (validation
       * popup + a TypeError). Variant picks stop here; the select and its form
       * still receive the event normally.
       */
      customElements.define(
        'houndkeep-upsells',
        class HoundkeepUpsells extends HTMLElement {
          connectedCallback() {
            if (this.stopChange) return;
            this.stopChange = function (event) {
              if (event.target && event.target.matches && event.target.matches('[data-hk-variant-input]')) {
                event.stopPropagation();
              }
            };
            this.addEventListener('change', this.stopChange);
          }

          disconnectedCallback() {
            if (this.stopChange) this.removeEventListener('change', this.stopChange);
            this.stopChange = null;
          }
        }
      );
    }

    if (customElements.get('houndkeep-bundle')) return;

    customElements.define(
      'houndkeep-bundle',
      class HoundkeepBundle extends HTMLElement {
        connectedCallback() {
          this.handleChange = this.handleChange.bind(this);
          this.addEventListener('change', this.handleChange);
          // The set can only be added with JavaScript (Shopify documents
          // multi-item adds for /cart/add.js only), so the button is rendered
          // hidden and revealed here; <noscript> explains the fallback.
          Array.prototype.slice.call(this.querySelectorAll('[data-hk-js-only]')).forEach(function (node) {
            node.hidden = false;
          });
          this.cartSubtotal = toNumber(this.getAttribute('data-hk-cart-subtotal'));
          var tierConfig = [];
          try {
            tierConfig = JSON.parse(this.getAttribute('data-hk-tiers') || '[]');
          } catch (e) {
            tierConfig = [];
          }
          this.tiers = normalizeTiers(tierConfig);
          this.projectionOn =
            this.getAttribute('data-hk-projection') === 'true' &&
            this.tiers.valid &&
            currencyAllowed(this.getAttribute('data-hk-currency'), this.getAttribute('data-hk-currencies'));
          if (this.projectionOn && hasPubSub()) {
            var self = this;
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
              var base = tierBase(event && event.cartData);
              if (base !== null) {
                self.cartSubtotal = base;
                self.update();
              } else {
                fetchCart()
                  .then(function (fresh) {
                    var freshBase = tierBase(fresh);
                    if (freshBase !== null) self.cartSubtotal = freshBase;
                    self.update();
                  })
                  .catch(function () {});
              }
            });
          }
          this.update();
        }

        disconnectedCallback() {
          this.removeEventListener('change', this.handleChange);
          if (this.unsubscribe) this.unsubscribe();
          this.unsubscribe = null;
        }

        handleChange(event) {
          if (event.target && event.target.matches && event.target.matches('[data-hk-variant-input]')) this.update();
        }

        money(cents) {
          return formatMoney(
            cents,
            this.getAttribute('data-hk-money-format'),
            this.getAttribute('data-hk-currency'),
            this.getAttribute('data-hk-locale')
          );
        }

        update() {
          var total = 0;
          var unavailable = [];
          Array.prototype.slice.call(this.querySelectorAll('[data-hk-line]')).forEach(function (line) {
            var input = line.querySelector('[data-hk-variant-input]');
            if (!input) return;
            var option = input.tagName === 'SELECT' ? input.options[input.selectedIndex] : input;
            if (!option) return;
            total += toNumber(option.getAttribute('data-hk-price')) || 0;
            if (option.getAttribute('data-hk-available') !== 'true') {
              unavailable.push(line.getAttribute('data-hk-title') || '');
            }
            var price = line.querySelector('[data-hk-line-price]');
            if (option.getAttribute('data-hk-price-formatted')) setText(price, option.getAttribute('data-hk-price-formatted'));
            var image = line.querySelector('[data-hk-line-image] img');
            var src = option.getAttribute('data-hk-image');
            if (image && src && image.getAttribute('src') !== src) {
              image.setAttribute('src', src);
              image.removeAttribute('srcset');
            }
          });

          setText(this.querySelector('[data-hk-bundle-total]'), this.money(total));

          var button = this.querySelector('[data-hk-bundle-submit]');
          var soldOutNode = this.querySelector('[data-hk-bundle-unavailable]');
          if (button) {
            if (unavailable.length) button.setAttribute('aria-disabled', 'true');
            else if (button.closest('form').getAttribute('data-hk-busy') !== 'true') button.removeAttribute('aria-disabled');
          }
          if (soldOutNode) {
            soldOutNode.hidden = !unavailable.length;
            setText(
              soldOutNode,
              unavailable.length
                ? fillTemplate(this.getAttribute('data-hk-msg-unavailable') || '', { products: unavailable.join(', ') })
                : ''
            );
          }

          var projectionNode = this.querySelector('[data-hk-bundle-projection]');
          if (!projectionNode) return;
          // No projection for a set that cannot be added as selected.
          if (!this.projectionOn || unavailable.length) {
            setText(projectionNode, '');
            return;
          }
          var projection = projectBundle(this.cartSubtotal, total, this.tiers.tiers);
          var message = '';
          if (projection.unlocksTier) {
            message = fillTemplate(this.getAttribute('data-hk-msg-unlocks'), {
              percent: projection.after.current.percent,
            });
          } else if (projection.after.next) {
            message = fillTemplate(this.getAttribute('data-hk-msg-away'), {
              amount: this.money(projection.after.remainingCents),
              percent: projection.after.next.percent,
            });
          }
          setText(projectionNode, message);
        }
      }
    );
  }

  function init() {
    var setup = function () {
      if (hasPubSub()) subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
      document.addEventListener('submit', onSubmit);
      // Click on an aria-disabled submit button must not submit natively either.
      document.addEventListener(
        'click',
        function (event) {
          var button = event.target && event.target.closest && event.target.closest('form[data-hk-add-form] [type="submit"]');
          if (button && button.getAttribute('aria-disabled') === 'true') event.preventDefault();
        },
        true
      );
      state.lastAnnounced = null;
      announceIfChanged();
      if (typeof MutationObserver !== 'undefined') {
        // Dawn swaps cart HTML asynchronously; announce once our panel changes.
        new MutationObserver(function (records) {
          var relevant = records.some(function (record) {
            return Array.prototype.some.call(record.addedNodes, function (node) {
              return (
                node.nodeType === 1 &&
                (node.matches('[data-hk-region]') || (node.querySelector && node.querySelector('[data-hk-region]')))
              );
            });
          });
          if (relevant) scheduleAnnounce();
        }).observe(document.body, { childList: true, subtree: true });
      }
    };
    defineElements();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
    else setup();
  }

  return {
    normalizeTiers: normalizeTiers,
    tierStatus: tierStatus,
    shippingStatus: shippingStatus,
    currencyAllowed: currencyAllowed,
    buildAddPayload: buildAddPayload,
    formatMoney: formatMoney,
    cartErrorMessage: cartErrorMessage,
    cartSignature: cartSignature,
    fillTemplate: fillTemplate,
    projectBundle: projectBundle,
    quantitiesByVariant: quantitiesByVariant,
    addedVariantIds: addedVariantIds,
    tierBase: tierBase,
    init: init,
  };
});
